import { FC, useEffect, useState } from "react";
import styled from "styled-components";
import useScore from "../../../hooks/useScore";
import useAxios from "../../../hooks/useAxios";
import { IAttendance, IGetHomeworkItems, IGetHomeworkScores } from "../../../models/tutor.model";
import { getHomeworkItemOptions, getHomeworkItems, getPbtScores, postPbtScores, putPbtScores } from "../../../api/tutor.api";
import _ from "lodash";
import useLoader from "../../../hooks/useLoader";
import { Select } from "antd";
import { useTranslation } from "react-i18next";
import Button from "../../Button";
import useNotification from "../../../hooks/useNotification";

const Block = styled.div`
  table {
    td {
      text-align: center;
    }

    .percentage-cell {
      display: flex;
      justify-content: space-between;

      div {
        width: 50%;
        text-align: center;
      }

      div:first-child {
        border-right: 1px solid #ddd;
      }
    }
  }
`;

interface IProps {
  timetableCode?: number;
  classCode?: string;
  attendances?: IAttendance[];
};

const PbtTable: FC<IProps> = ({
  timetableCode,
  classCode,
  attendances = [],
}) => {
  const { t } = useTranslation();
  const { successNotification } = useNotification();
  const { handleData } = useScore();
  const { data: res, isLoading, error, execute } = useAxios<IGetHomeworkItems>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetHomeworkScores>();
  const { arr: res3, isLoading: isLoading3, error: error3, executeArr: execute3 } = useAxios<any>();
  const { arr: submitRes, isLoading: submitIsLoading, error: submitError, executeArr: submitExecute } = useAxios();
  useLoader(
    isLoading || isLoading2 || isLoading3 || submitIsLoading,
    [error, error2, error3, submitError],
  );

  const [data, setData] = useState<any>([]);
  const [options, setOptions] = useState<any>([]);
  const [scores, setScores] = useState<any>([]);

  useEffect(() => {
    if (timetableCode) {
      setData([]);
      execute(getHomeworkItems(timetableCode, 'PB'));
      doGetPbtScores();
    }
  }, [timetableCode]);

  useEffect(() => {
    if (res) {
      setData(handleData(res.data));

      let evaluationTypeCodes: number[] = res.data?.map(item => item.evaluationTypeCode);
      evaluationTypeCodes = _.uniq(evaluationTypeCodes);

      const arr: any = [];
      evaluationTypeCodes.forEach(evaluationTypeCode => {
        arr.push(getHomeworkItemOptions(evaluationTypeCode));
      });
      execute3(arr);
    }
  }, [res]);

  useEffect(() => {
    if (res3?.length) {
      const options: any = [];
      res3.forEach(item => {
        options.push({
          evaluationTypeCode: item.data?.at(0)?.evaluationTypeCode,
          data: item.data,
        });
      });
      setOptions(options);
    }
  }, [res3]);

  useEffect(() => {
    if (res2 && attendances.length && data.length) {
      const newScores: any = [];

      attendances.forEach(() => {
        newScores.push([...Array(data.length).fill(undefined)]);
      });

      attendances.forEach((item, index) => {
        const scoreInfo = res2?.data?.find(item2 => item2.memberCode === item.memberCode)?.studentScoreInfo;
        if (scoreInfo) {
          newScores[index] = scoreInfo;
        }
      });

      newScores.forEach((student: any) => {
        student.forEach((score: any) => {
          if(score) {
            if (score.testYn === 'N') {
              score.correctCount = null
            }
          }
        });
      });
      setScores(newScores);
    }
  }, [res2, attendances, data]);

  useEffect(() => {
    if (submitRes.length) {
      doGetPbtScores();
      successNotification(t('common.message.save1'));
    }
  }, [submitRes]);

  const doGetPbtScores = () => {
    if (timetableCode) {
      execute2(getPbtScores(timetableCode));
    }
  };

  const getDefaultValue = (
    evaluationTypeCode?: number,
    evaluationConversionCode?: number
  ) => {
    if (evaluationTypeCode === 1000 && !evaluationConversionCode) {
      return options.find((item: any) => item.evaluationTypeCode === 1000)?.data?.at(0)?.evaluationConversionCode;
    }

    return undefined;
  };

  const onSetScore = (index: number, subindex: number, value: number) => {
    const newScores: any = _.cloneDeep(scores);
    newScores[index][subindex].evaluationConversionCode = value;
    setScores(newScores);
  };

  const getOptions = (evaluationTypeCode?: number) => {
    if (!evaluationTypeCode) {
      return null;
    }

    const selectOptions = options.find((item: any) => item.evaluationTypeCode === evaluationTypeCode);
    if (selectOptions) {
      return selectOptions.data?.map((item: any) => (
        <Select.Option
          key={item.evaluationConversionCode}
          value={item.evaluationConversionCode}
        >
          {item.evaluationInput}
        </Select.Option>
      ))
    }

    return null;
  };

  const onSetCorrectCount = (index: number, subindex: number, value: string) => {
    if (isNaN(Number(value))) {
      return;
    }
    const questionCount = scores.at(index)?.at(subindex)?.questionCount    
    if (value > questionCount) {
      return;
    }

    const newScores: any = _.cloneDeep(scores);
    newScores[index][subindex].correctCount = value ? Number(value) : '';
    setScores(newScores);
  };

  const getCorrectCountInput = (index: number, subindex: number) => {
    let correctCount = scores.at(index)?.at(subindex)?.correctCount;
    if (!correctCount && correctCount !== 0) {
      correctCount = '';
    }
    return (
      <>
        <input
          type="text"
          className="form-control"
          value={correctCount}
          onChange={e => onSetCorrectCount(index, subindex, e.target.value)}
          style={{ width: '40px' }}
        />
        <span className="v-line px-1">/ {scores.at(index)?.at(subindex)?.questionCount}</span>
      </>
    )
  };

  const getPercentage = (index: number, subindex: number) => {
    const correctCount = scores.at(index)?.at(subindex)?.correctCount;
    const questionCount = scores.at(index)?.at(subindex)?.questionCount;
    const testYn = scores.at(index)?.at(subindex)?.testYn;

    if (correctCount === 0) {
      return '0'
    }
    if (typeof correctCount === 'number' && typeof questionCount === 'number') {
      return `${Math.round(correctCount / questionCount * 100)}%`;
    }
    if (testYn === 'N') {
      return (<span style={{ color: 'red' }}>{t('tutor.class.1')}</span>);
    }

    return ''
  };

  const onSubmit = () => {
    scores.forEach((student: any, studentIndex: number) => {
      const attendance = attendances.at(studentIndex);
      student.forEach((item: any, index: number) => {
        const evaluationTypeCode = data.at(index)?.evaluationTypeCode;
        const typeCode = data.at(index)?.inputTypeCode;
        if (evaluationTypeCode === 1000 && !item.evaluationConversionCode) {
          item.evaluationConversionCode = getDefaultValue(evaluationTypeCode, undefined);
        } else if (typeCode === 'PC') {
          if (!item.correctCount && item.correctCount !== 0) {
            item.correctCount = 0;
            item.testYn = 'N';  
          } else {
            item.testYn = 'Y';
          }
        }
        item.memberCode = attendance?.memberCode;
        item.classStudentCode = attendance?.classStudentCode;
      })
    })

    const postReq: any = [];
    const putReq: any = [];
    scores.forEach((student: any) => {
      student.forEach((score: any) => {
        if (score.scoreCode) {
          putReq.push(score);
        } else {
          postReq.push(score);
        }
      })
    })

    const arr = [];
    if (postReq.length) {
      arr.push(postPbtScores(postReq));
    }
    if (putReq.length) {
      arr.push(putPbtScores(putReq));
    }
    if (arr.length) {
      submitExecute(arr);
    }
  };

  return (
    <>
    <Block className="comm-table__area line-type">
      <table>
        <colgroup>
          {[...Array((data.length || 0) + 1)].map((_, index) => (
            <col key={index} width="200px" />
          ))}
        </colgroup>
        <thead>
          {data.map((item: any, index: number) => {
            if (item.colSpan) {
              return (
                <tr>
                  <th rowSpan={3} />
                  <th
                    key={index}
                    colSpan={item.colSpan}
                  >
                    {item.depth1Name}
                  </th>
                </tr>
              );
            }
            return null;
          })}
          <tr>
            {data.map((item: any, index: number) => (
              <th key={index}>{item.subjectName}</th>
            ))}
          </tr>
          <tr>
            {data.map((item: any, index: number) => (
              <th key={index}>{item.depth2Name}</th>
            ))}
          </tr>
          <tr>
            <th>{t('tutor.class.2')}</th>
            {data.map((item: any, index: number) => (
              <th key={index}>
                {
                  item.inputTypeCode === 'PC'
                    ? (
                      <div className="percentage-cell">
                        <div>{t('tutor.class.3')}</div>
                        <div>{t('tutor.class.4')}</div>
                      </div>
                    )
                    : item.evaluationTypeName
                }
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {
            attendances.map((student: any, index: number) => (
              <tr key={index}>
                <td>{student.studentKorName} ({student.studentEngName})</td>
                {data.map((item: any, subindex: number) => (
                  <td key={subindex}>
                    {
                      item.inputTypeCode === 'PC'
                        ? (
                          <div className="percentage-cell">
                            <div className="flex justify-content-center">
                              {getCorrectCountInput(index, subindex)}
                            </div>
                            <div className="v-line">
                              {getPercentage(index, subindex)}
                            </div>
                          </div>
                          )
                        : (
                          <Select
                            className="w-100"
                            placeholder={t('common.button.select')}
                            value={
                              scores.at(index)?.at(subindex)?.evaluationConversionCode ||
                              getDefaultValue(res?.data?.at(subindex)?.evaluationTypeCode, scores.at(index)?.at(subindex)?.evaluationConversionCode)
                            }
                            onChange={value => onSetScore(index, subindex, value)}
                          >
                            {getOptions(res?.data?.at(subindex)?.evaluationTypeCode)}
                          </Select>
                          )
                    }
                  </td>
                ))}
              </tr>
            ))
          }
        </tbody>
      </table>
    </Block>
    <div className="comm-bx__space">
      <div className="comm-btn__area right">
        <Button
          onClick={onSubmit}
          className="btn-basic pink"
          text={t('common.button.save')}
        />
      </div>
    </div>
    </>
  );
};

export default PbtTable;